import { useLayoutEffect, useRef } from 'react';
import { useActionData, useLoaderData } from 'react-router-dom';
import { breakpointDownQuery } from '@/utils/mediaQueries';

// AIDEV-NOTE: Below `lg` the TwoColumnForm columns stack, pushing the annotated
// sample invoice off-screen. Both queries must stay tied to the same breakpoint.
const MOBILE_QUERY = breakpointDownQuery('lg');

/**
 * Scrolls a card to the top of the viewport on mobile-sized screens, so that
 * content below it (such as the annotated sample invoice) is within reach
 * instead of sitting off-screen.
 *
 * On mount the scroll is unconditional. After each completed lookup it re-runs,
 * but only re-anchors when the alert is not fully visible — so a failed lookup
 * can never leave its error off-screen, while the user's scroll position is
 * otherwise left alone.
 *
 * Returns a ref for the card to scroll to and a ref for the alert to measure.
 */
export default function useScrollIntoViewOnMobile() {
  const cardRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnMount = useRef(false);

  // AIDEV-NOTE: These change identity exactly once per completed lookup (the GET
  // loader on Pay Invoice, the POST action on Add Location). Do not key this off
  // useNavigation().state — when a lookup settles quickly React commits only the
  // final 'idle' render, the dep never changes, and the re-anchor silently never
  // runs.
  const loaderData = useLoaderData<unknown>();
  const actionData = useActionData<unknown>();

  // AIDEV-NOTE: useLayoutEffect (not useEffect) so the page paints already
  // scrolled rather than visibly snapping down after the first paint.
  useLayoutEffect(() => {
    if (!window.matchMedia(MOBILE_QUERY).matches) {
      return;
    }

    const card = cardRef.current;

    if (!card) {
      return;
    }

    // After the initial scroll, only re-anchor when an alert is present and
    // clipped. A partially visible alert counts as clipped: a half-cut error
    // message is as easy to miss as one that is fully off-screen.
    if (hasScrolledOnMount.current && isFullyVisible(alertRef.current)) {
      return;
    }

    hasScrolledOnMount.current = true;

    // AIDEV-NOTE: Scroll the document explicitly rather than card.scrollIntoView().
    // TwoColumnForm wraps the cards in an `overflow-hidden` div, which counts as a
    // scroll container, so scrollIntoView would walk it before scrolling the page.
    window.scrollTo({
      top: window.scrollY + card.getBoundingClientRect().top,
      behavior: 'auto',
    });
  }, [loaderData, actionData]);

  return { cardRef, alertRef };
}

/**
 * Whether the element is entirely within the viewport. A missing element counts
 * as visible, since there is nothing the user could be failing to see.
 */
function isFullyVisible(element: HTMLElement | null) {
  if (!element) {
    return true;
  }

  const { top, bottom } = element.getBoundingClientRect();

  // AIDEV-NOTE: window.innerHeight does not shrink for the on-screen keyboard on
  // iOS Safari. Acceptable here because the keyboard dismisses on submit, which
  // is the only time this runs.
  return top >= 0 && bottom <= window.innerHeight;
}
